import { LARGE_ITEM_HEIGHT, LARGE_ITEM_WIDTH } from 'components/Wizards/Wizard/Form/TemplateSelect/TemplateSelect'
import { IFilters } from '../Types'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { collectResults } from 'utils/query'
import Styles from '../TemplatesV2.module.scss'
import { TemplatePreview } from 'components/Wizards/Wizard/Form/TemplateSelect/TemplatePreview'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import cx from 'classnames'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { Text } from 'blaze-ui/Text/Text'
import { EmptyState } from 'components/Wizards/Wizard/Form/TemplateSelect/EmptyState'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const WIDE_ASPECT_RATIO = 1.5
const NARROW_ASPECT_RATIO = 0.7

export const getTemplateDisplayName = (templateName: string) => templateName.split(' - ')[0]

interface Props {
  onSelect: (templateId: number, aspectRatioKey: AspectRatioKey) => void
  filters: Omit<IFilters, 'aspectRatioKey'>
  aspectRatioKey: AspectRatioKey
  useCompactUI?: boolean
  selectedIds?: number[]
}

export const TemplatesGrid = ({ onSelect, filters, aspectRatioKey, useCompactUI = false, selectedIds = [] }: Props) => {
  const { width, height } = ASPECT_RATIOS[aspectRatioKey]
  const isWide = width / height > WIDE_ASPECT_RATIO
  const isNarrow = !isWide && width / height < NARROW_ASPECT_RATIO
  const itemWidth = isWide ? undefined : LARGE_ITEM_WIDTH
  const itemHeight = isWide ? LARGE_ITEM_HEIGHT : undefined
  const { isMobile } = useResponsiveDesign()
  const ITEMS_TO_LOAD = isMobile ? 12 : 36
  const SHIMMER_PREVIEWS = isMobile ? 2 : 8

  const query = useVisualTemplates(
    {
      element_type: 'Template',
      items: ITEMS_TO_LOAD,
      type: filters.types === 'all' ? undefined : ([...filters.types] as string[]),
      style: filters.styles === 'all' ? undefined : ([...filters.styles] as string[]),
      category: filters.categories === 'all' ? undefined : ([...filters.categories] as string[]),
      ...filters,
      width,
      height,
      order_by: 'random',
    },
    {
      staleTime: Infinity,
    },
  )

  const templates = collectResults(query, (data) => data.visual_elements)
  const { fetchNextPage, hasNextPage } = query

  if (!query.isLoading && templates.length === 0) {
    return (
      <div className={Styles.emptyTemplateGridContainer}>
        <EmptyState />
      </div>
    )
  }

  return (
    <>
      <div
        className={cx(Styles.templatesGridContainer, {
          [Styles.wide]: isWide,
          [Styles.narrow]: isNarrow && !useCompactUI,
          [Styles.compact]: useCompactUI,
        })}
      >
        {(!query.isFetched || query.isLoading) &&
          Array.from({ length: SHIMMER_PREVIEWS }).map((_, i) => (
            <ShimmeringColor aspectRatio={width / height} width="100%" key={i} />
          ))}
        {query.isFetched &&
          !query.isLoading &&
          templates.length > 0 &&
          templates.map((template, i) => (
            <div
              className={cx(Styles.templateContainer, {
                [Styles.wide]: isWide,
                [Styles.compact]: useCompactUI,
              })}
              key={`${template.id}-${i}`}
            >
              <TemplatePreview
                template={template}
                onPress={() => {
                  onSelect(template.id, aspectRatioKey)
                }}
                aspectRatio={width / height}
                itemWidth={itemWidth}
                itemHeight={itemHeight}
                grow
                growHeight={isWide}
                isSelected={selectedIds.includes(template.id)}
              />
              <div className={Styles.templateLabelContainer}>
                <div className={Styles.templateNameContainer}>
                  <Text variant="smallList" lineClamp={useCompactUI ? 2 : 1}>
                    {getTemplateDisplayName(template.name)}
                  </Text>
                </div>
                {!useCompactUI && <Text variant="metadata">{ASPECT_RATIOS[aspectRatioKey].name} by Blaze.ai</Text>}
              </div>
            </div>
          ))}
      </div>
      {hasNextPage && (
        <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
          <div
            className={cx(Styles.templatesGridContainer, {
              [Styles.wide]: isWide,
              [Styles.narrow]: isNarrow && !useCompactUI,
              [Styles.compact]: useCompactUI,
            })}
          >
            {Array.from({ length: SHIMMER_PREVIEWS }).map((_, i) => (
              <ShimmeringColor aspectRatio={width / height} width="100%" key={i} />
            ))}
          </div>
        </InfiniteScroll>
      )}
    </>
  )
}
